import React, { useRef } from 'react';
import { Box, Image, Skeleton } from '@landr/maestro';
import { getAssetImageAndSrcSet } from 'helpers/getAssetImageAndSrcSet';
import { t } from '@lingui/macro';
import { css, cx } from '@landr/styled-system/css';
import useDetectTargetIsInsideViewport from 'hooks/useDetectTargetIsInsideViewport/useDetectTargetIsInsideViewport';
import {
    BackgroundImageFromAssetProps,
    BackgroundImagePositionType,
} from './types';

// Base styles for background image using Panda CSS
const backgroundImageBaseStyles = css({
    position: 'absolute',
    objectFit: 'cover',
    objectPosition: 'center center',
    height: '101%', // prevent 1px border under certain ratio
    width: '101%',
});

// Position variants mapping using Panda CSS
const positionStyles: Record<BackgroundImagePositionType, string> = {
    centerCenter: css({ objectPosition: 'center center' }),
    topCenter: css({ objectPosition: 'center top' }),
    bottomCenter: css({ objectPosition: 'center bottom' }),
    centerLeft: css({ objectPosition: 'left center' }),
    centerRight: css({ objectPosition: 'right center' }),
    topLeft: css({ objectPosition: 'left top' }),
    topRight: css({ objectPosition: 'right top' }),
    bottomLeft: css({ objectPosition: 'left bottom' }),
    bottomRight: css({ objectPosition: 'right bottom' }),
};

const BackgroundImageFromAsset: React.FC<BackgroundImageFromAssetProps> = ({
    image,
    filetype = 'jpg',
    bgPosition = 'centerCenter',
    dimensions = ['2x'],
    isLoading = false,
    alt = t`Background Image`,
    lazyLoading = false,
    opacity = 'opaque',
    objectFit = 'cover',
    zoom = 1,
    loading = 'lazy',
}) => {
    const ref = useRef(null);
    const isInViewport = useDetectTargetIsInsideViewport({ ref, offset: 500 });
    const isVisible = lazyLoading ? isInViewport : true;

    const { fullImage, srcSet } = getAssetImageAndSrcSet(
        image,
        filetype,
        dimensions,
        isVisible,
    );

    const dynamicStyles = css({
        width: `calc(${zoom} * 101%)`,
        height: `calc(${zoom} * 101%)`,
    });

    if (isLoading) {
        return (
            <Skeleton
                width="100%"
                height="100%"
                className={css({
                    position: 'absolute',
                })}
            />
        );
    }

    return (
        <Box ref={ref}>
            <Image
                alt={alt}
                src={isVisible ? fullImage : ''}
                srcSet={isVisible ? srcSet : ''}
                objectFit={objectFit}
                opacity={opacity}
                loading={loading}
                className={cx(
                    backgroundImageBaseStyles,
                    positionStyles[bgPosition],
                    dynamicStyles,
                )}
            />
        </Box>
    );
};

export default BackgroundImageFromAsset;
